import { useState, useEffect } from "react";
import type {
  CCAMActe,
  CCAMFavoriteActe,
  CCAMSearchFilters,
} from "cadendar-shared";
import { CCAMSearchBar } from "./CCAMSearchBar";
import { CCAMSearchResults } from "./CCAMSearchResults";
import { useCCAMSearch } from "./hooks/useCCAMSearch";
import { useCCAMFavorites } from "./hooks/useCCAMFavorites";

export interface CCAMBrowserModalProps {
  /** Controlled visibility */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** Selection callback */
  onSelectActe: (acte: CCAMActe) => void;
  /** Context mode */
  mode?: "selection" | "configuration";
  /** Configurable title */
  title?: string;
  /** Auto-close behavior (default: true) */
  autoCloseOnSelect?: boolean;
}

type TabType = "recherche" | "favoris" | "associations";

export const CCAMBrowserModal = ({
  isOpen,
  onClose,
  onSelectActe,
  // mode = "selection",
  title = "Browser CCAM",
  autoCloseOnSelect = true,
}: CCAMBrowserModalProps) => {
  const [activeTab, setActiveTab] = useState<TabType>("recherche");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState<CCAMSearchFilters>({
    excludeExpired: true,
  });
  const [favoriteSearchQuery, setFavoriteSearchQuery] = useState("");
  const [selectedFavoriteActe, setSelectedFavoriteActe] =
    useState<CCAMActe | null>(null);
  const [customLabel, setCustomLabel] = useState("");
  const [customFeesInput, setCustomFeesInput] = useState("");
  const [favoriteFormError, setFavoriteFormError] = useState<string | null>(
    null,
  );
  const [favoriteFormSuccess, setFavoriteFormSuccess] = useState<string | null>(
    null,
  );

  // tRPC search query with debounce and infinite scroll
  const searchResult = useCCAMSearch(searchQuery, searchFilters);
  const favoriteSearchResult = useCCAMSearch(favoriteSearchQuery, {
    excludeExpired: true,
  });
  const favorites = useCCAMFavorites();

  // Reset page when query changes
  useEffect(() => {
    if (searchResult.resetPage) {
      searchResult.resetPage();
    }
  }, [searchQuery, searchFilters]);

  const handleSelectActe = (acte: CCAMActe) => {
    onSelectActe(acte);
    if (autoCloseOnSelect) {
      onClose();
    }
  };

  const handleSelectFavoriteBaseActe = (acte: CCAMActe) => {
    setSelectedFavoriteActe(acte);
    setCustomLabel(acte.libelle);
    setCustomFeesInput("");
    setFavoriteFormError(null);
    setFavoriteFormSuccess(null);
  };

  const handleEditFavorite = (favorite: CCAMFavoriteActe) => {
    setSelectedFavoriteActe({
      code: favorite.ccamCode,
      libelle: favorite.ccamLabel,
      acteType: "",
      tarif: favorite.customFees ?? 0,
      dateEffet: "",
      dateFin: null,
      ccamVersion: "",
      allowsExtraFees: favorite.allowsExtraFees,
    });
    setCustomLabel(favorite.customLabel);
    setCustomFeesInput(
      favorite.customFees !== null ? favorite.customFees.toString() : "",
    );
    setFavoriteFormError(null);
    setFavoriteFormSuccess(null);
  };

  const handleDeleteFavorite = async (favorite: CCAMFavoriteActe) => {
    setFavoriteFormError(null);
    setFavoriteFormSuccess(null);
    try {
      await favorites.deleteFavorite({ ccamCode: favorite.ccamCode });
      setFavoriteFormSuccess("Favori supprimé.");
      if (selectedFavoriteActe?.code === favorite.ccamCode) {
        setSelectedFavoriteActe(null);
        setCustomLabel("");
        setCustomFeesInput("");
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Suppression impossible.";
      setFavoriteFormError(message);
    }
  };

  const handleSubmitFavorite = async () => {
    setFavoriteFormError(null);
    setFavoriteFormSuccess(null);

    if (!selectedFavoriteActe) {
      setFavoriteFormError("Sélectionnez d'abord un acte CCAM.");
      return;
    }

    const label = customLabel.trim();
    if (!label) {
      setFavoriteFormError("L'intitulé personnalisé est obligatoire.");
      return;
    }

    const allowsExtraFees = selectedFavoriteActe.allowsExtraFees === true;
    const trimmedFees = customFeesInput.trim();
    const parsedFees =
      trimmedFees.length === 0
        ? null
        : Number.parseFloat(trimmedFees.replace(",", "."));

    if (parsedFees !== null && Number.isNaN(parsedFees)) {
      setFavoriteFormError("Les honoraires doivent être un nombre valide.");
      return;
    }

    if (parsedFees !== null && parsedFees < 0) {
      setFavoriteFormError("Les honoraires doivent être positifs.");
      return;
    }

    if (parsedFees !== null && !allowsExtraFees) {
      setFavoriteFormError(
        "Cet acte CCAM n'autorise pas les honoraires libres.",
      );
      return;
    }

    try {
      await favorites.upsertFavorite({
        ccamCode: selectedFavoriteActe.code,
        customLabel: label,
        customFees: parsedFees,
      });
      setFavoriteFormSuccess("Favori enregistré.");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Enregistrement impossible.";
      setFavoriteFormError(message);
    }
  };

  const handleUseFavorite = (favorite: CCAMFavoriteActe) => {
    handleSelectActe({
      code: favorite.ccamCode,
      libelle: favorite.customLabel,
      acteType: "",
      tarif: favorite.customFees ?? 0,
      dateEffet: "",
      dateFin: null,
      ccamVersion: "",
      allowsExtraFees: favorite.allowsExtraFees,
    });
  };

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  const tabs: Array<{ id: TabType; label: string }> = [
    { id: "recherche", label: "Recherche" },
    { id: "favoris", label: "Mes Favoris" },
    { id: "associations", label: "Mes Associations" },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 bg-white"
    >
      {/* Header */}
      <header className="flex items-center justify-between border-b p-4">
        <button
          onClick={onClose}
          className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
          aria-label="Fermer le navigateur CCAM"
        >
          <span>←</span>
          <span>Retour</span>
        </button>
        <h1 id="modal-title" className="text-2xl font-bold">
          {title}
        </h1>
        <div className="w-24" /> {/* Spacer for center alignment */}
      </header>

      {/* Tabs */}
      <div className="border-b">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8" aria-label="Onglets">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                aria-label={`Onglet ${tab.label}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="h-[calc(100vh-64px)] overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">
          {activeTab === "recherche" && (
            <div>
              <CCAMSearchBar
                onSearchChange={setSearchQuery}
                filters={searchFilters}
                onFiltersChange={setSearchFilters}
                isLoading={searchResult.isLoading}
              />
              <CCAMSearchResults
                results={searchResult.data?.actes || []}
                onSelectActe={handleSelectActe}
                isLoading={searchResult.isLoading}
                query={searchQuery}
                hasMore={searchResult.hasMore}
                onLoadMore={searchResult.loadMore}
              />
            </div>
          )}
          {activeTab === "favoris" && (
            <div className="space-y-8">
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Définir un acte favori personnalisé
                </h2>
                <p className="text-sm text-gray-600">
                  Sélectionnez un acte CCAM, puis définissez l'intitulé à
                  afficher sur les devis. Les honoraires sont modifiables
                  uniquement si l'acte l'autorise.
                </p>

                <CCAMSearchBar
                  onSearchChange={setFavoriteSearchQuery}
                  filters={{ excludeExpired: true }}
                  isLoading={favoriteSearchResult.isLoading}
                />

                <CCAMSearchResults
                  results={favoriteSearchResult.data?.actes || []}
                  onSelectActe={handleSelectFavoriteBaseActe}
                  isLoading={favoriteSearchResult.isLoading}
                  query={favoriteSearchQuery}
                  hasMore={favoriteSearchResult.hasMore}
                  onLoadMore={favoriteSearchResult.loadMore}
                />

                {selectedFavoriteActe && (
                  <div className="border rounded-lg p-4 space-y-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        Acte CCAM sélectionné
                      </p>
                      <p className="font-mono text-sm text-blue-600">
                        {selectedFavoriteActe.code}
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedFavoriteActe.libelle}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Honoraires libres:{" "}
                        {selectedFavoriteActe.allowsExtraFees ? "Oui" : "Non"}
                      </p>
                    </div>

                    <div className="space-y-3">
                      <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">
                          Intitulé personnalisé (devis)
                        </span>
                        <input
                          type="text"
                          value={customLabel}
                          onChange={(e) => setCustomLabel(e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Ex: Détartrage complet cabinet"
                        />
                      </label>

                      <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">
                          Honoraires personnalisés (€)
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={customFeesInput}
                          onChange={(e) => setCustomFeesInput(e.target.value)}
                          disabled={!selectedFavoriteActe.allowsExtraFees}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={
                            selectedFavoriteActe.allowsExtraFees
                              ? "Ex: 75.00"
                              : "Honoraires non autorisés"
                          }
                        />
                      </label>

                      <button
                        onClick={handleSubmitFavorite}
                        disabled={favorites.isSaving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-60"
                      >
                        {favorites.isSaving
                          ? "Enregistrement..."
                          : "Enregistrer le favori"}
                      </button>

                      {favoriteFormError && (
                        <p className="text-sm text-red-600">
                          {favoriteFormError}
                        </p>
                      )}
                      {favoriteFormSuccess && (
                        <p className="text-sm text-green-700">
                          {favoriteFormSuccess}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Mes favoris enregistrés
                </h3>
                {favorites.isLoading ? (
                  <p className="text-sm text-gray-500">
                    Chargement des favoris...
                  </p>
                ) : favorites.favorites.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Aucun favori pour le moment.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {favorites.favorites.map((favorite) => (
                      <div
                        key={favorite.id}
                        className="border rounded-lg p-3 flex items-start justify-between gap-4"
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-blue-600">
                            {favorite.ccamCode}
                          </p>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {favorite.customLabel}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            Base CCAM: {favorite.ccamLabel}
                          </p>
                          <p className="text-xs text-gray-500">
                            Honoraires:{" "}
                            {favorite.customFees !== null
                              ? `${favorite.customFees.toFixed(2)} €`
                              : "Non définis"}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUseFavorite(favorite)}
                            className="px-3 py-1 text-sm border rounded text-blue-700 border-blue-300"
                          >
                            Utiliser
                          </button>
                          <button
                            onClick={() => handleEditFavorite(favorite)}
                            className="px-3 py-1 text-sm border rounded text-gray-700 border-gray-300"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteFavorite(favorite)}
                            disabled={favorites.isDeleting}
                            className="px-3 py-1 text-sm border rounded text-red-700 border-red-300 disabled:opacity-60"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {favorites.mutationError && !favoriteFormError && (
                  <p className="text-sm text-red-600">
                    {favorites.mutationError.message}
                  </p>
                )}
              </section>
            </div>
          )}
          {activeTab === "associations" && (
            <div className="text-gray-500">
              <p>Mes associations d'actes (à implémenter dans story 4.2)</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
